/**
 * Pure helpers for the Contacts workspace.
 *
 * Everything here is derived from the Contact documents the API already
 * returns (see models/Contact.js): there is no client-side state and no
 * network access, so the search/sort/group pipeline can be unit tested
 * without rendering the page.
 */

/** How the contact entered the network, derived from the two schema flags. */
export function contactSource(contact) {
  if (contact?.fromInvitation) return 'invitation';
  if (contact?.isManual) return 'manual';
  return 'tap';
}

/** Linked Velox user id for a contact row, if any. */
export function contactLinkedUserId(contact) {
  const linked = contact?.contact?._id || contact?.contact;
  return linked ? String(linked) : null;
}

/**
 * Whether Retract should be offered: either you marked the card as sent, or
 * the API reported that they already hold a copy of your card.
 */
export function canRetractCard(contact) {
  if (contact?.contactDetailsSent) return true;
  if (!contactLinkedUserId(contact)) return false;
  return Boolean(contact?.theyHaveMyCard);
}

export const SOURCE_META = {
  tap: { label: 'Tap', full: 'Saved from a card tap' },
  invitation: { label: 'Invite', full: 'Added from an invitation' },
  manual: { label: 'Manual', full: 'Added manually' },
};

export function contactDisplayName(contact) {
  const name = String(contact?.name || '').trim();
  return name || 'Unnamed contact';
}

/** Up to two initials, skipping punctuation-only tokens. */
export function contactInitials(name) {
  const parts = String(name || '')
    .replace(/[^\p{L}\p{N}\s'-]/gu, ' ')
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Restrained, on-brand avatar palette. A wall of identical grey discs is hard
 * to scan, so each contact gets a stable hue from a curated set rather than a
 * generated one — the tints stay muted enough to read as one family.
 */
const AVATAR_TINTS = [
  [198, 167, 94], // gold
  [110, 143, 184], // slate blue
  [126, 156, 135], // sage
  [176, 132, 105], // clay
  [155, 135, 176], // mauve
  [106, 156, 156], // teal
];

export function avatarTint(seed) {
  const key = String(seed || '');
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = (hash * 31 + key.charCodeAt(i)) % 100000;
  }
  const [r, g, b] = AVATAR_TINTS[hash % AVATAR_TINTS.length];
  return {
    background: `rgba(${r}, ${g}, ${b}, 0.14)`,
    ring: `rgba(${r}, ${g}, ${b}, 0.42)`,
    foreground: `rgb(${r}, ${g}, ${b})`,
  };
}

/** The schema carries three candidate timestamps; prefer the most specific. */
export function contactAddedAt(contact) {
  const raw = contact?.connectedAt || contact?.dateAdded || contact?.createdAt;
  if (!raw) return null;
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
}

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

export function formatRelativeDate(date, now = new Date()) {
  if (!date) return '';
  const diff = now.getTime() - date.getTime();
  if (diff < 0) return formatShortDate(date, now);
  if (diff < MINUTE) return 'Just now';
  if (diff < HOUR) return `${Math.floor(diff / MINUTE)}m ago`;
  if (diff < DAY) return `${Math.floor(diff / HOUR)}h ago`;
  if (diff < 2 * DAY) return 'Yesterday';
  if (diff < 7 * DAY) return `${Math.floor(diff / DAY)}d ago`;
  return formatShortDate(date, now);
}

export function formatShortDate(date, now = new Date()) {
  if (!date) return '';
  const sameYear = date.getFullYear() === now.getFullYear();
  return date.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}

export function formatFullDate(date) {
  if (!date) return '';
  return date.toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/* ------------------------------------------------------------------ search */

export function contactSearchFields(contact) {
  return [
    contact?.name,
    contact?.company,
    contact?.role,
    contact?.title,
    contact?.email,
    contact?.phone,
    contact?.linkedinURL,
    contact?.additionalURL,
    contact?.notes,
  ].filter(Boolean);
}

/** Every whitespace-separated term must match somewhere ("ana bank"). */
export function matchesQuery(contact, query) {
  const terms = String(query || '')
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
  if (terms.length === 0) return true;
  const haystack = contactSearchFields(contact).join(' \u0000 ').toLowerCase();
  return terms.every((term) => haystack.includes(term));
}

/* ----------------------------------------------------------------- options */

export const QUICK_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'flagged', label: 'Flagged' },
  { id: 'sent', label: 'Card sent' },
  { id: 'pending', label: 'Awaiting card' },
  { id: 'located', label: 'With location' },
  { id: 'noted', label: 'With notes' },
];

export const SOURCE_FILTERS = [
  { id: 'any', label: 'Any source' },
  { id: 'tap', label: 'Card taps' },
  { id: 'invitation', label: 'Invitations' },
  { id: 'manual', label: 'Added manually' },
];

export const SORT_OPTIONS = [
  { id: 'recent', label: 'Newest first' },
  { id: 'oldest', label: 'Oldest first' },
  { id: 'name', label: 'Name A–Z' },
  { id: 'nameDesc', label: 'Name Z–A' },
  { id: 'company', label: 'Company A–Z' },
];

export const GROUP_OPTIONS = [
  { id: 'none', label: 'No grouping' },
  { id: 'date', label: 'When added' },
  { id: 'company', label: 'Company' },
  { id: 'letter', label: 'Initial' },
];

export function quickFilterPredicate(id) {
  switch (id) {
    case 'flagged':
      return (c) => Boolean(c?.isFlagged);
    case 'sent':
      return (c) => Boolean(c?.contactDetailsSent);
    case 'pending':
      return (c) => !c?.contactDetailsSent && !c?.fromInvitation;
    case 'located':
      return (c) => typeof c?.lat === 'number' && typeof c?.lng === 'number';
    case 'noted':
      return (c) => Boolean(String(c?.notes || '').trim());
    case 'all':
    default:
      return () => true;
  }
}

export function filterContacts(contacts, { query = '', quickFilter = 'all', source = 'any', removedIds } = {}) {
  const list = Array.isArray(contacts) ? contacts : [];
  const matchesQuick = quickFilterPredicate(quickFilter);
  return list.filter((contact) => {
    if (!contact) return false;
    if (removedIds && contact._id && removedIds.has(contact._id)) return false;
    if (!matchesQuick(contact)) return false;
    if (source !== 'any' && contactSource(contact) !== source) return false;
    return matchesQuery(contact, query);
  });
}

const collator = typeof Intl !== 'undefined' ? new Intl.Collator(undefined, { sensitivity: 'base' }) : null;

function compareText(a, b) {
  const left = String(a || '');
  const right = String(b || '');
  // Contacts with no company or name sort last rather than to the top.
  if (!left && right) return 1;
  if (left && !right) return -1;
  return collator ? collator.compare(left, right) : left.localeCompare(right);
}

function timeOf(contact) {
  const date = contactAddedAt(contact);
  return date ? date.getTime() : 0;
}

export function sortContacts(contacts, sortId = 'recent') {
  const list = (Array.isArray(contacts) ? contacts : []).slice();
  switch (sortId) {
    case 'oldest':
      return list.sort((a, b) => timeOf(a) - timeOf(b));
    case 'name':
      return list.sort((a, b) => compareText(a?.name, b?.name) || timeOf(b) - timeOf(a));
    case 'nameDesc':
      return list.sort((a, b) => compareText(b?.name, a?.name) || timeOf(b) - timeOf(a));
    case 'company':
      return list.sort(
        (a, b) => compareText(a?.company, b?.company) || compareText(a?.name, b?.name)
      );
    case 'recent':
    default:
      return list.sort((a, b) => timeOf(b) - timeOf(a));
  }
}

function dateBucket(date, now) {
  if (!date) return { key: 'undated', label: 'No date recorded', order: 1000 };
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const time = date.getTime();
  if (time >= startOfToday) return { key: 'today', label: 'Today', order: 0 };
  if (time >= startOfToday - DAY) return { key: 'yesterday', label: 'Yesterday', order: 1 };
  if (time >= startOfToday - 7 * DAY) return { key: 'week', label: 'This week', order: 2 };
  if (time >= startOfToday - 30 * DAY) return { key: 'month', label: 'This month', order: 3 };
  // Older than a month: label with the calendar year so the section is concrete.
  const year = date.getFullYear();
  const thisYear = now.getFullYear();
  return { key: `y-${year}`, label: String(year), order: 4 + Math.max(0, thisYear - year) };
}

/**
 * Group an already-sorted list. Groups keep the incoming order of their first
 * member so the active sort still drives the reading order, except for date
 * buckets which always run newest to oldest.
 */
export function groupContacts(contacts, groupId = 'none', now = new Date()) {
  const list = Array.isArray(contacts) ? contacts : [];
  if (groupId === 'none' || list.length === 0) {
    return [{ key: 'all', label: null, items: list }];
  }

  const buckets = new Map();
  const push = (key, label, order, contact) => {
    if (!buckets.has(key)) buckets.set(key, { key, label, order, items: [] });
    buckets.get(key).items.push(contact);
  };

  list.forEach((contact, index) => {
    if (groupId === 'date') {
      const bucket = dateBucket(contactAddedAt(contact), now);
      push(bucket.key, bucket.label, bucket.order, contact);
      return;
    }
    if (groupId === 'company') {
      const company = String(contact?.company || '').trim();
      push(company ? company.toLowerCase() : '__none__', company || 'No company', company ? index : Number.MAX_SAFE_INTEGER, contact);
      return;
    }
    const initial = contactInitials(contact?.name).charAt(0);
    const letter = /\p{L}/u.test(initial) ? initial : '#';
    push(letter, letter, index, contact);
  });

  const groups = Array.from(buckets.values());
  if (groupId === 'date') return groups.sort((a, b) => a.order - b.order);
  if (groupId === 'letter') return groups.sort((a, b) => compareText(a.label, b.label));
  return groups.sort((a, b) => a.order - b.order || compareText(a.label, b.label));
}

/* ------------------------------------------------------------- edit fields */

export const EDITABLE_FIELDS = [
  { id: 'name', label: 'Name', type: 'text', autoComplete: 'name', required: true },
  { id: 'email', label: 'Email', type: 'email', autoComplete: 'email', required: true },
  { id: 'phone', label: 'Phone', type: 'tel', autoComplete: 'tel' },
  { id: 'company', label: 'Company', type: 'text', autoComplete: 'organization' },
  { id: 'role', label: 'Role', type: 'text', autoComplete: 'organization-title' },
  { id: 'title', label: 'Title', type: 'text', hint: 'e.g. Dr, Prof' },
  { id: 'linkedinURL', label: 'LinkedIn', type: 'url', inputMode: 'url' },
  { id: 'additionalURL', label: 'Website', type: 'url', inputMode: 'url' },
];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function validateContactDraft(draft) {
  const errors = {};
  if (!String(draft?.name || '').trim()) errors.name = 'A name is required.';
  const email = String(draft?.email || '').trim();
  if (!email) errors.email = 'An email address is required.';
  else if (!EMAIL_PATTERN.test(email)) errors.email = 'Enter a valid email address.';
  return errors;
}

export function contactDraftFrom(contact) {
  return EDITABLE_FIELDS.reduce((acc, field) => {
    acc[field.id] = contact?.[field.id] ? String(contact[field.id]) : '';
    return acc;
  }, {});
}

/** Trim every value and drop the keys that did not change. */
export function changedDraftFields(draft, contact) {
  const changed = {};
  EDITABLE_FIELDS.forEach(({ id }) => {
    const next = String(draft?.[id] ?? '').trim();
    const prev = String(contact?.[id] ?? '').trim();
    if (next !== prev) changed[id] = next;
  });
  return changed;
}

/** Absolute http(s) href for a possibly schemeless stored URL. */
export function externalHref(raw) {
  const value = String(raw || '').trim();
  if (!value) return null;
  const href = /^https?:\/\//i.test(value) ? value : `https://${value}`;
  try {
    const url = new URL(href);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    return url.href;
  } catch {
    return null;
  }
}

export function prettyUrl(raw) {
  const href = externalHref(raw);
  if (!href) return String(raw || '');
  return href.replace(/^https?:\/\//i, '').replace(/\/$/, '');
}

/** Timeline rows for the detail panel, built only from fields we actually hold. */
export function contactActivity(contact, now = new Date()) {
  if (!contact) return [];
  const events = [];
  const added = contactAddedAt(contact);
  const source = contactSource(contact);

  events.push({
    id: 'added',
    title: SOURCE_META[source].full,
    meta: added ? `${formatFullDate(added)} · ${formatRelativeDate(added, now)}` : 'Date not recorded',
    tone: 'primary',
  });

  if (typeof contact.lat === 'number' && typeof contact.lng === 'number') {
    events.push({ id: 'location', title: 'Meeting location captured', meta: 'Shown on the map below' });
  }
  if (contact.contactDetailsSent) {
    events.push({
      id: 'sent',
      title: 'Your card was sent',
      meta: contact.inviteSentByEmail ? 'Delivered by email' : 'Shared from Velox',
    });
  }
  if (String(contact.notes || '').trim()) {
    events.push({ id: 'notes', title: 'You added a private note', meta: 'Only visible to you' });
  }
  if (contact.isFlagged) {
    events.push({ id: 'flag', title: 'Flagged for follow-up', meta: 'Appears in your flagged view' });
  }
  return events;
}

/** Single .vcf holding one VCARD block per contact (valid per RFC 2426). */
export function summariseContact(contact) {
  return [
    contactDisplayName(contact),
    contact?.role || null,
    contact?.company || null,
    contact?.email || null,
    contact?.phone || null,
  ]
    .filter(Boolean)
    .join('\n');
}
